import type { Page } from '../../app/feature';
import type { ApiActivity, ApiCartela, ApiPlayer } from '../../shared/types';
import { delegate, esc, inputValue, q, qa, setInputValue } from '../../shared/dom';
import { getLang, t } from '../../shared/i18n';
import { dateTime, fmt, money } from '../../shared/format';
import { ApiError } from '../../shared/api/http';
import { getState } from '../../shared/store/store';
import { hydrateFromApi } from '../../shared/store/sync';
import { toast } from '../../shared/ui/toast';
import { href } from '../../app/router';
import { isAdmin } from '../../shared/session/session';
import { missionLabelOf } from '../../shared/store/selectors';
import { playersApi } from './players.api';

let playerId: string | null = null;
let player: ApiPlayer | null = null;
let cartelas: ApiCartela[] = [];
let activities: ApiActivity[] = [];
/** Filtro da grade de números por missão (null = todas). */
let filter: string | null = null;
let showAll = false;

const ACT_ICON: Record<string, string> = { DEPOSIT: '💰', BET_CASINO: '🎰', BET_SPORTS: '⚽', TOURNAMENT: '🏆', REFERRAL: '🤝' };
const PAGE_SIZE = 40;

/**
 * Ficha do jogador: quem é, os números que tem (em bilhetes, filtráveis por missão), a linha do
 * tempo do que ele fez na casa e, para o suporte, a emissão manual de compensação.
 */
export const playerDetail: Page = {
  id: 'jugador',
  pattern: '/jugadores/:id',
  roles: ['ADMIN', 'SUPPORT'],
  navParent: 'jugadores',

  template: () => `
<div class="crumbs"><a class="auth-link" href="${href('/jugadores')}" data-i18n="pl.back">← Jugadores</a></div>
<div id="pd-missing" class="panel" hidden><div class="empty" data-i18n="pd.notFound">Este jugador ya no existe.</div></div>

<div id="pd-body">
  <div class="panel hero pd-hero">
    <div class="hero-main pd-hero-main">
      <div class="pd-avatar" id="pd-avatar">?</div>
      <div>
        <h2 id="pd-name" class="hero-title">—</h2>
        <div class="pd-meta" id="pd-meta"></div>
      </div>
    </div>
    <div class="stats hero-stats">
      <div style="--accent:var(--orange)"><b id="pd-count">0</b><span data-i18n="kpi.cartelas">Cartelas activas</span><small data-i18n="pl.kpiActive">participan en el próximo sorteo</small></div>
      <div style="--accent:var(--gold)"><b id="pd-total">0</b><span data-i18n="kpi.issued2">Cartelas emitidas</span><small data-i18n="pl.kpiTotal">en toda la historia</small></div>
      <div style="--accent:var(--green)"><b id="pd-won">0</b><span data-i18n="pl.prizes">Premios ganados</span><small data-i18n="pd.wonHint">números que salieron en la rueda</small></div>
      <div style="--accent:var(--line-2)"><b id="pd-acts">0</b><span data-i18n="pd.acts">Eventos</span><small data-i18n="pd.actsHint">acciones registradas en la casa</small></div>
    </div>
  </div>

  <div class="cols" style="margin-top:18px">
    <!-- números -->
    <div class="panel span-8">
      <div class="panel-head">
        <h3 data-i18n="cart.numbers">Números del jugador</h3>
        <div class="spacer"></div>
        <div class="pd-legend">
          <span><i class="pd-dot" style="background:var(--green)"></i><span data-i18n="st.winner">Ganador</span></span>
          <span><i class="pd-dot" style="background:var(--gold)"></i><span data-i18n="pd.playedWeekly">ya jugó la semanal</span></span>
          <span><i class="pd-dot" style="background:var(--txt-3)"></i><span data-i18n="pd.closed">cerrado</span></span>
        </div>
      </div>
      <div class="pd-filters" id="pd-filters"></div>
      <div class="pd-numbers" id="pd-numbers"></div>
      <div class="inline" style="justify-content:center; margin-top:12px" id="pd-more-wrap" hidden>
        <button class="btn btn-sm btn-ghost" id="pd-more"></button>
      </div>
    </div>

    <!-- atividade -->
    <div class="panel span-4">
      <div class="panel-head"><h3 data-i18n="pd.activity">Actividad en la casa</h3></div>
      <p class="hint" data-i18n="pd.activityHint2">Lo que la Rueda leyó de la Weebet para este jugador. Cada evento entra una sola vez: por eso no se duplican números.</p>
      <ol class="pd-timeline" id="pd-timeline"></ol>
    </div>

    <!-- emissão manual (compensação) -->
    <div class="panel span-12 pd-manual" id="pd-manual-panel">
      <div class="panel-head"><h3 data-i18n="cart.manualTitle">Emisión manual</h3><span class="chip chip-dim" style="margin-left:8px" data-i18n="pd.manualTag">compensación</span></div>
      <p class="hint" data-i18n="pd.manualHint2">Sólo para corregir un problema (ej.: un número que no salió por error de la casa). Los números normales nacen solos de los eventos. El motivo queda registrado en cada número.</p>
      <form id="pd-issue" class="row" autocomplete="off" novalidate style="align-items:end; margin-top:12px">
        <div class="field" style="flex:2 1 220px"><label for="pd-mission" data-i18n="mis.mission">Misión</label><select id="pd-mission"></select></div>
        <div class="field" style="flex:0 1 110px"><label for="pd-qty" data-i18n="f.qty">Cantidad</label><input type="number" id="pd-qty" min="1" max="50" value="1"></div>
        <div class="field" style="flex:3 1 260px"><label for="pd-note" data-i18n="cart.reason">Motivo</label><input type="text" id="pd-note" placeholder="Compensación por error en la live"></div>
        <div class="field" style="flex:0 0 auto"><button type="submit" class="btn btn-primary" data-i18n="cart.issue">Emitir cartelas</button></div>
      </form>
      <div class="auth-error" id="pd-issue-error" role="alert"></div>
    </div>
  </div>
</div>`,

  mount() {
    q<HTMLFormElement>('#pd-issue').addEventListener('submit', async e => {
      e.preventDefault();
      const error = q('#pd-issue-error'); error.textContent = '';
      if (!player) return;
      const note = inputValue('#pd-note'), qty = Math.max(1, parseInt(inputValue('#pd-qty'), 10) || 1);
      if (note.length < 3) { error.textContent = t('cart.needReason'); return; }
      try {
        const issued = await playersApi.issue({ playerId: player.id, missionId: q<HTMLSelectElement>('#pd-mission').value || undefined, qty, note });
        toast(`${issued.length} ${t('msg.issued')}`);
        setInputValue('#pd-note', '');
        await reload();
        void hydrateFromApi();
      } catch (err) {
        error.textContent = err instanceof ApiError ? err.message : t('auth.errGeneric');
      }
    });

    delegate(q('#pd-filters'), 'click', '[data-filter]', el => {
      filter = el.dataset.filter || null;
      showAll = false;
      paintNumbers();
    });
    q('#pd-more').onclick = () => { showAll = !showAll; paintNumbers(); };
  },

  render(params) {
    const id = params.id ?? null;
    const sel = q<HTMLSelectElement>('#pd-mission');
    const keep = sel.value;
    sel.innerHTML = `<option value="">${esc(t('cart.noMission'))}</option>` + getState().missions.map(m => `<option value="${m.id}">${esc(missionLabelOf(m))}</option>`).join('');
    if (keep) sel.value = keep;
    q('#pd-manual-panel').hidden = !isAdmin();
    if (id !== playerId) { playerId = id; player = null; cartelas = []; activities = []; filter = null; showAll = false; paint(); void reload(); }
    else paint();
  }
};

async function reload(): Promise<void> {
  if (!playerId) return;
  try {
    [player, cartelas, activities] = await Promise.all([playersApi.get(playerId), playersApi.cartelas({ playerId }), playersApi.activities(playerId)]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) { q('#pd-missing').hidden = false; q('#pd-body').hidden = true; return; }
    toast(err instanceof ApiError ? err.message : t('auth.errGeneric'));
  }
  paint();
}

function paint(): void {
  q('#pd-missing').hidden = true;
  q('#pd-body').hidden = false;
  q('#pd-name').textContent = player?.name ?? '…';
  q('#pd-avatar').textContent = initials(player?.name ?? '');
  q('#pd-meta').innerHTML = player ? [
    `<span class="chip chip-dim">ID ${esc(player.externalId)}</span>`,
    player.email ? `<span class="muted">✉ ${esc(player.email)}</span>` : '',
    `<span class="muted">${esc(t('pl.since'))} ${dateTime(Date.parse(player.createdAt))}</span>`,
  ].filter(Boolean).join('') : '';

  const active = cartelas.filter(c => c.status === 'ACTIVE').length;
  q('#pd-count').textContent = fmt(active);
  q('#pd-total').textContent = fmt(cartelas.length);
  q('#pd-won').textContent = fmt(cartelas.filter(c => c.status === 'WON').length);
  q('#pd-acts').textContent = fmt(activities.length);

  paintNumbers();
  paintTimeline();
}

/** Cor estável por missão (0..5), igual ao site do jogador. */
function accentOf(key: string | null | undefined): number {
  let h = 0;
  for (const ch of key ?? '') h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 6;
}

function paintNumbers(): void {
  const lang = getLang();
  const label = (c: ApiCartela): string => c.mission ? (lang === 'pt' ? c.mission.labelPt : c.mission.labelEs) : t('src.MANUAL');

  // filtros: distribuição por missão
  const groups = new Map<string, { key: string; label: string; count: number }>();
  for (const c of cartelas) {
    const key = c.mission?.key ?? '';
    const g = groups.get(key) ?? { key, label: label(c), count: 0 };
    g.count++; groups.set(key, g);
  }
  const chips = [...groups.values()].sort((a, b) => b.count - a.count);
  q('#pd-filters').innerHTML = cartelas.length ? [
    `<button type="button" class="pd-filter ${filter === null ? 'on' : ''}" data-filter="">${esc(t('rueda.all'))} <b>${cartelas.length}</b></button>`,
    ...chips.map(g => `<button type="button" class="pd-filter ${filter === g.key ? 'on' : ''}" data-filter="${esc(g.key)}"><i class="pd-dot acc-${accentOf(g.key)}"></i>${esc(g.label)} <b>${g.count}</b></button>`),
  ].join('') : '';

  const list = filter === null ? cartelas : cartelas.filter(c => (c.mission?.key ?? '') === filter);
  const shown = showAll ? list : list.slice(0, PAGE_SIZE);
  q('#pd-numbers').innerHTML = shown.map(c => `
    <div class="pd-ticket acc-${accentOf(c.mission?.key)} ${c.status === 'WON' ? 'won' : ''} ${c.status === 'VOID' || c.status === 'EXPIRED' ? 'off' : ''} ${c.weeklyEventId && c.status === 'ACTIVE' ? 'played' : ''}"
         title="${esc(label(c))}${c.activity ? ` · ${esc(t('act.' + c.activity.kind))}${c.activity.value ? ' ' + money(c.activity.value) : ''}` : c.note ? ` · ${esc(c.note)}` : ''} · ${dateTime(Date.parse(c.createdAt))}">
      <b>${c.number}</b>
      <small>${esc(label(c))}</small>
      ${c.status === 'WON' ? `<em>${esc(t('st.winner'))}</em>` : c.weeklyEventId && c.status === 'ACTIVE' ? `<em>${esc(t('pd.toMonthly'))}</em>` : c.status !== 'ACTIVE' ? `<em>${esc(t('pd.closed'))}</em>` : ''}
    </div>`).join('') || `<div class="empty">${esc(t('pd.noNumbers'))}</div>`;

  const wrap = q('#pd-more-wrap');
  wrap.hidden = list.length <= PAGE_SIZE;
  q('#pd-more').textContent = showAll ? t('pd.showLess') : t('pd.showAll').replace('{n}', String(list.length));
}

function paintTimeline(): void {
  q('#pd-timeline').innerHTML = activities.map(a => `
    <li class="pd-event">
      <i class="pd-event-icon">${ACT_ICON[a.kind] ?? '•'}</i>
      <div class="pd-event-body">
        <b>${esc(t('act.' + a.kind))}${a.value ? ` <span class="pd-event-value">${money(a.value)}</span>` : ''}</b>
        <span>${dateTime(Date.parse(a.occurredAt))} · ${esc(t('src.' + a.source))}</span>
      </div>
      <span class="pd-event-qty ${a.cartelas ? '' : 'zero'}">${a.cartelas ? '+' + a.cartelas : '0'}</span>
    </li>`).join('') || `<li class="empty">${esc(t('pd.noActivity'))}</li>`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase();
}

// evita aviso de import não usado quando não há filtros renderizados
void qa;
